import { retrieveConfig } from '../config/config'

/**
 * Default timeout configuration
 */
const DEFAULT_TIMEOUT_CONFIG = {
    enabled: true,
    timeout: 60 * 60 * 1000, // 1 hour (60 minutes)
    warningTime: 2 * 60 * 1000, // 2 minutes warning before timeout
    events: [],
    modal: {
        title: 'Session Timeout Warning',
        message: 'Session expiring due to inactivity. Click "Extend Session" to extend the session for another 15 minutes.',
        extendButtonText: 'Extend Session',
        primaryColor: '#007bff',
        backgroundColor: '#ffffff',
        textColor: '#333333',
        overlayColor: 'rgba(0, 0, 0, 0.5)',
    },
    timeoutScreen: {
        title: 'Session Expired',
        message: 'Your session has expired due to inactivity. This window will close automatically.',
        countdownMessage: 'Closing in {countdown} seconds...',
        autoCloseDelay: 5000, // 5 seconds
    },
    // Separate configuration for payment link expired scenarios
    paymentLinkExpiredScreen: {
        title: 'Payment Link Expired',
        message: 'Your session has expired, please go back to your merchant and regenerate a payment link.',
        countdownMessage: 'Closing in {countdown} seconds...',
        autoCloseDelay: 5000, // 5 seconds
    },
}

/**
 * Get session timeout configuration from merchant config or use defaults
 * @returns {Promise<Object>} Timeout configuration object
 */
export const getSessionTimeoutConfig = async () => {
    try {
        const merchantConfig = await retrieveConfig()

        // Check if merchant has custom timeout configuration
        const timeoutConfig = merchantConfig?.sessionTimeout || {}

        // eslint-disable-next-line no-console
        console.log('timeoutConfig', timeoutConfig)

        // Merge with defaults
        const config = {
            enabled: timeoutConfig.enabled !== undefined ? timeoutConfig.enabled : DEFAULT_TIMEOUT_CONFIG.enabled,
            timeout: timeoutConfig.timeout || DEFAULT_TIMEOUT_CONFIG.timeout,
            warningTime: timeoutConfig.warningTime || DEFAULT_TIMEOUT_CONFIG.warningTime,
            events: timeoutConfig.events || DEFAULT_TIMEOUT_CONFIG.events,
            dynamicTimeout: {
                ...DEFAULT_TIMEOUT_CONFIG.dynamicTimeout,
                ...timeoutConfig.dynamicTimeout,
            },
            modal: {
                ...DEFAULT_TIMEOUT_CONFIG.modal,
                ...timeoutConfig.modal,
                // Apply merchant theme colors if available
                primaryColor:
                    timeoutConfig.modal?.primaryColor ||
                    merchantConfig?.colorTheme?.primaryColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.primaryColor,
                textColor:
                    timeoutConfig.modal?.textColor ||
                    merchantConfig?.colorTheme?.text ||
                    merchantConfig?.textColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.textColor,
            },
            timeoutScreen: {
                ...DEFAULT_TIMEOUT_CONFIG.timeoutScreen,
                ...timeoutConfig.timeoutScreen,
                // Apply merchant theme colors to timeout screen as well
                primaryColor:
                    timeoutConfig.timeoutScreen?.primaryColor ||
                    timeoutConfig.modal?.primaryColor ||
                    merchantConfig?.colorTheme?.primaryColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.primaryColor,
                textColor:
                    timeoutConfig.timeoutScreen?.textColor ||
                    timeoutConfig.modal?.textColor ||
                    merchantConfig?.colorTheme?.text ||
                    merchantConfig?.textColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.textColor,
                backgroundColor:
                    timeoutConfig.timeoutScreen?.backgroundColor ||
                    timeoutConfig.modal?.backgroundColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.backgroundColor,
            },
            // Payment link expired screen configuration
            paymentLinkExpiredScreen: {
                ...DEFAULT_TIMEOUT_CONFIG.paymentLinkExpiredScreen,
                ...timeoutConfig.paymentLinkExpiredScreen,
                // Apply merchant theme colors to payment link expired screen
                primaryColor:
                    timeoutConfig.paymentLinkExpiredScreen?.primaryColor ||
                    timeoutConfig.timeoutScreen?.primaryColor ||
                    timeoutConfig.modal?.primaryColor ||
                    merchantConfig?.colorTheme?.primaryColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.primaryColor,
                textColor:
                    timeoutConfig.paymentLinkExpiredScreen?.textColor ||
                    timeoutConfig.timeoutScreen?.textColor ||
                    timeoutConfig.modal?.textColor ||
                    merchantConfig?.colorTheme?.text ||
                    merchantConfig?.textColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.textColor,
                backgroundColor:
                    timeoutConfig.paymentLinkExpiredScreen?.backgroundColor ||
                    timeoutConfig.timeoutScreen?.backgroundColor ||
                    timeoutConfig.modal?.backgroundColor ||
                    DEFAULT_TIMEOUT_CONFIG.modal.backgroundColor,
            },
        }

        return config
    } catch (error) {
        console.warn('Failed to retrieve session timeout config, using defaults:', error)
        return DEFAULT_TIMEOUT_CONFIG
    }
}

/**
 * Convert timeout duration from different formats to milliseconds
 * @param {number|string} duration - Duration in various formats
 * @returns {number} Duration in milliseconds
 */
export const parseDuration = duration => {
    if (typeof duration === 'number') {
        return duration
    }

    if (typeof duration === 'string') {
        const match = duration.match(/^(\d+)(ms|s|m|h)?$/i)
        if (match) {
            const value = parseInt(match[1], 10)
            const unit = (match[2] || 'ms').toLowerCase()

            switch (unit) {
                case 'ms':
                    return value
                case 's':
                    return value * 1000
                case 'm':
                    return value * 60 * 1000
                case 'h':
                    return value * 60 * 60 * 1000
                default:
                    return value
            }
        }
    }

    return DEFAULT_TIMEOUT_CONFIG.timeout
}

/**
 * Validate timeout configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validated configuration
 */
export const validateTimeoutConfig = config => {
    const validated = { ...config }

    // Ensure timeout is at least 1 minute
    if (validated.timeout < 60000) {
        console.warn('Timeout duration too short, setting to minimum 1 minute')
        validated.timeout = 60000
    }

    // Ensure warning time is less than timeout and at least 30 seconds
    if (validated.warningTime >= validated.timeout) {
        console.warn('Warning time must be less than timeout duration')
        validated.warningTime = Math.max(30000, validated.timeout - 60000)
    }

    if (validated.warningTime < 30000) {
        console.warn('Warning time too short, setting to minimum 30 seconds')
        validated.warningTime = 30000
    }

    // Ensure events array is not empty - use proper defaults
    if (!Array.isArray(validated.events) || validated.events.length === 0) {
        console.warn('No events specified for activity detection, using defaults')
        validated.events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    }

    return validated
}

/**
 * Log session timeout event to backend (if endpoint exists)
 * @param {string} eventType - Type of timeout event ('warning', 'timeout', 'extended')
 * @param {Object} data - Additional event data
 */
// eslint-disable-next-line no-unused-vars
export const logTimeoutEvent = async (eventType, data = {}) => {
    // const logData = {
    //     eventType,
    //     timestamp: new Date().toISOString(),
    //     userAgent: navigator.userAgent,
    //     url: window.location.href,
    //     ...data,
    // }

    // Console log for debugging, very spammy, uncomment when needed
    // console.debug('[Session Timeout Event]', logData)

    try {
        const API_BASE_URL = window.env.REACT_APP_DEMO_BACKEND_URL

        if (!API_BASE_URL) {
            // eslint-disable-next-line no-console
            console.log('[Session Timeout] No API_BASE_URL configured, skipping backend logging')
            return
        }

        // Only log if the backend supports session timeout logging
        // TODO review again and remove if not needed
        // const response = await fetch(`${API_BASE_URL}/session/timeout-event`, {
        //     method: 'POST',
        //     headers: {
        //         'Content-Type': 'application/json',
        //         domain: window.location.hostname,
        //     },
        //     body: JSON.stringify(logData),
        // }).catch(error => {
        //     // eslint-disable-next-line no-console
        //     console.log('[Session Timeout] Backend logging failed:', error.message)
        //     // Silently fail if endpoint doesn't exist
        // })
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('[Session Timeout] Error during backend logging:', error.message)
        // Silently fail - logging is optional
    }
}

export default {
    getSessionTimeoutConfig,
    parseDuration,
    validateTimeoutConfig,
    logTimeoutEvent,
    DEFAULT_TIMEOUT_CONFIG,
}
